import { PDFDocument } from 'pdf-lib';

export interface CompressionResult {
  compressedFile: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  success: boolean;
  error?: string;
}

export interface CompressionOptions {
  quality: 'high' | 'medium' | 'low';
  removeMetadata?: boolean;
}

/**
 * Compress a PDF file while maintaining good quality
 * 
 * This uses pdf-lib to:
 * 1. Parse and re-serialize the PDF (removes redundant data)
 * 2. Optionally remove metadata
 * 3. Optimize object streams
 * 
 * Note: pdf-lib doesn't support image recompression, but it does
 * optimize the PDF structure which can reduce file size significantly
 * for many documents.
 */
export async function compressPdf(
  file: File,
  options: CompressionOptions = { quality: 'high' }
): Promise<CompressionResult> {
  const originalSize = file.size;
  
  try {
    // Read the file as ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();
    
    // Load the PDF
    const pdfDoc = await PDFDocument.load(arrayBuffer, {
      ignoreEncryption: true,
      updateMetadata: false,
    });
    
    // Remove metadata if requested (can save a few KB)
    if (options.removeMetadata || options.quality === 'low') {
      pdfDoc.setTitle('');
      pdfDoc.setAuthor('');
      pdfDoc.setSubject('');
      pdfDoc.setKeywords([]);
      pdfDoc.setProducer('pdfaa.ai');
      pdfDoc.setCreator('pdfaa.ai');
    }
    
    // Save with optimization options
    const compressedBytes = await pdfDoc.save({
      useObjectStreams: true, // Compress objects into streams
      addDefaultPage: false,
      // Note: pdf-lib doesn't have built-in image compression
      // For that, we'd need a server-side solution or a different library
    });
    
    // Create a new File from the compressed bytes
    const compressedBlob = new Blob([new Uint8Array(compressedBytes)], { type: 'application/pdf' });
    const compressedFile = new File([compressedBlob], file.name, { type: 'application/pdf' });
    
    const compressedSize = compressedFile.size;
    const compressionRatio = ((originalSize - compressedSize) / originalSize) * 100;
    
    // If compression didn't help (or made it larger), return original
    if (compressedSize >= originalSize) {
      return {
        compressedFile: file,
        originalSize,
        compressedSize: originalSize,
        compressionRatio: 0,
        success: true,
      };
    }
    
    return {
      compressedFile,
      originalSize,
      compressedSize,
      compressionRatio,
      success: true,
    };
  } catch (error) {
    // Compression failed -- silent
    return {
      compressedFile: file,
      originalSize,
      compressedSize: originalSize,
      compressionRatio: 0,
      success: false,
      error: error instanceof Error ? error.message : 'Compression failed',
    };
  }
}

/**
 * Check if a file is a valid PDF
 */
export async function isValidPdf(file: File): Promise<boolean> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const header = new Uint8Array(arrayBuffer.slice(0, 5));
    
    // Check for PDF magic bytes: %PDF-
    return (
      header[0] === 0x25 && // %
      header[1] === 0x50 && // P
      header[2] === 0x44 && // D
      header[3] === 0x46 && // F
      header[4] === 0x2d    // -
    );
  } catch {
    return false;
  }
}

/**
 * Get PDF page count
 */
export async function getPdfPageCount(file: File): Promise<number> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer, {
      ignoreEncryption: true,
    });
    return pdfDoc.getPageCount();
  } catch {
    return 0;
  }
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
